import { stripOptionPrefix } from "@/utils/text-format"
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  HStack,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AppEmptyState } from "../../Common/AppEmptyState"
import { HtmlContent } from "../../Common/HtmlContent"
import { emptyStateCopy } from "../../Common/emptyStateCopy"

import { useColorModeValue } from "../../ui/color-mode"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog"
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerRoot,
  DrawerTrigger,
} from "../../ui/drawer"
import { Radio, RadioGroup } from "../../ui/radio"

import { FiHelpCircle, FiList } from "react-icons/fi"
import { StudentTimerControl } from "../StudentTimer/StudentTimerControl"
import { AnnotatableContainer } from "./AnnotatableContent"
import {
  QuestionNavigator,
  useNavigatorTriggerCompact,
} from "./QuestionNavigator"
import {
  type QuizData,
  QuizInterfaceProvider,
  type QuizResults,
  useQuizInterface,
} from "./QuizInterfaceContext"
import { getSelectionOffsetsWithin } from "./domAnnotations"
import type { AnnotationKind, QuestionAnnotations } from "./textAnnotations"
import {
  addAnnotationToBlock,
  clearAllQuestionAnnotations,
  readQuestionAnnotations,
  removeAnnotationFromBlock,
  writeQuestionAnnotations,
} from "./textAnnotations"

const UNANSWERED_SUBMIT_COUNTDOWN_SECONDS = 3

interface QuizInterfaceProps {
  quiz: QuizData
  attemptId: string
  timeLimit?: number | null
  initialAnswers?: Record<string, number>
  initialTimeRemaining?: number | null
  initialQuestionIndex?: number
  onAnswerSelect: (
    questionId: string,
    answerIndex: number,
    timeSpent: number,
  ) => void | Promise<void>
  onSubmit: (params?: {
    finalQuestionId: string
    finalAnswerIndex: number
    timeSpent: number
  }) => void
  isSubmitting?: boolean
  isSubmitted?: boolean
  results?: QuizResults
  isLoading?: boolean
  error?: Error | null
  onQuestionChange?: (index: number) => void
  showRationaleMode?: string
  onNavigateToReview?: (params: {
    questionIndex: number
    questionId: string
  }) => void
  /** Show the student's opt-in personal pacing timer (real attempts only). */
  enableStudentTimer?: boolean
}

function QuizInterfaceContent({
  isSubmitting = false,
  isSubmitted = false,
  results,
  isLoading = false,
  error = null,
  showRationaleMode,
  quiz,
  attemptId,
  enableStudentTimer = false,
}: Omit<
  QuizInterfaceProps,
  | "onAnswerSelect"
  | "onSubmit"
  | "onQuestionChange"
  | "onNavigateToReview"
  | "timeLimit"
  | "initialAnswers"
  | "initialTimeRemaining"
  | "initialQuestionIndex"
>) {
  const {
    quizState,
    selectedAnswerIndex,
    showConfirmSubmit,
    currentQuestion,
    totalQuestions,
    answeredCount,
    unansweredCount,
    handleAnswerSelect,
    handleNext,
    handlePrevious,
    handleSubmit,
    isSavingAnswer,
    answerSaveError,
    setShowConfirmSubmit,
    formatTime,
  } = useQuizInterface()

  const selectedBg = useColorModeValue("blue.50", "blue.900")
  const selectedBorderColor = useColorModeValue("blue.500", "blue.200")
  const defaultBorderColor = useColorModeValue("gray.200", "gray.600")
  const hoverBorderColor = useColorModeValue("blue.300", "blue.200")
  const hoverBg = useColorModeValue("blue.100", "whiteAlpha.200")
  const optionTextColor = useColorModeValue("gray.900", "whiteAlpha.900")

  const [navigatorOpen, setNavigatorOpen] = useState(false)
  const {
    isCompact: compactNavigatorTrigger,
    triggerRef: navigatorTriggerRef,
  } = useNavigatorTriggerCompact()
  const questionCardRef = useRef<HTMLDivElement | null>(null)
  const [questionCardHeight, setQuestionCardHeight] = useState<number>()

  useEffect(() => {
    const el = questionCardRef.current
    if (!el) return

    const update = () => {
      const next = Math.ceil(el.getBoundingClientRect().height)
      setQuestionCardHeight(next > 0 ? next : undefined)
    }

    update()

    if (typeof ResizeObserver === "undefined") {
      return
    }

    const observer = new ResizeObserver(() => update())
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const currentQuestionId = currentQuestion?.id
  const hasSelectedAnswer =
    selectedAnswerIndex !== null ||
    (currentQuestionId !== undefined &&
      quizState.answers[currentQuestionId] !== undefined)
  const shouldDisableNextButton =
    showRationaleMode === "after_each" && !hasSelectedAnswer

  const [showUnansweredConfirm, setShowUnansweredConfirm] = useState(false)
  const [submitCountdown, setSubmitCountdown] = useState(0)
  const unansweredQuestionCount = Math.max(
    totalQuestions - Object.keys(quizState.answers).length,
    0,
  )

  const openSubmitConfirmation = useCallback(() => {
    setSubmitCountdown(
      unansweredQuestionCount > 0 ? UNANSWERED_SUBMIT_COUNTDOWN_SECONDS : 0,
    )
    setShowConfirmSubmit(true)
  }, [setShowConfirmSubmit, unansweredQuestionCount])

  useEffect(() => {
    if (!showConfirmSubmit || submitCountdown <= 0) {
      return
    }

    const timer = window.setInterval(() => {
      setSubmitCountdown((current) => Math.max(current - 1, 0))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [showConfirmSubmit, submitCountdown])

  // Refs for stable access inside keyboard effect
  const hasSelectedAnswerRef = useRef(hasSelectedAnswer)
  hasSelectedAnswerRef.current = hasSelectedAnswer
  const isLastQuestionRef = useRef(
    quizState.currentQuestionIndex >= quiz.questions.length - 1,
  )
  isLastQuestionRef.current =
    quizState.currentQuestionIndex >= quiz.questions.length - 1
  const showConfirmSubmitRef = useRef(showConfirmSubmit)
  showConfirmSubmitRef.current = showConfirmSubmit
  const showUnansweredConfirmRef = useRef(showUnansweredConfirm)
  showUnansweredConfirmRef.current = showUnansweredConfirm
  const handleNextRef = useRef(handleNext)
  handleNextRef.current = handleNext
  const handleSubmitRef = useRef(handleSubmit)
  handleSubmitRef.current = handleSubmit
  const openSubmitConfirmationRef = useRef(openSubmitConfirmation)
  openSubmitConfirmationRef.current = openSubmitConfirmation
  const submitCountdownRef = useRef(submitCountdown)
  submitCountdownRef.current = submitCountdown
  const handleAnswerSelectRef = useRef(handleAnswerSelect)
  handleAnswerSelectRef.current = handleAnswerSelect
  const isSavingAnswerRef = useRef(isSavingAnswer)
  isSavingAnswerRef.current = isSavingAnswer
  const currentOptionsCountRef = useRef(currentQuestion?.options.length ?? 0)
  currentOptionsCountRef.current = currentQuestion?.options.length ?? 0
  const handlePreviousRef = useRef(handlePrevious)
  handlePreviousRef.current = handlePrevious
  const canGoBackRef = useRef(false)
  canGoBackRef.current =
    quizState.currentQuestionIndex > 0 &&
    !(
      showRationaleMode === "after_each" &&
      Object.keys(quizState.answers).length > 0
    )

  const [annotations, setAnnotations] = useState<QuestionAnnotations>({})
  const annotationsRef = useRef<QuestionAnnotations>({})
  annotationsRef.current = annotations

  const storageKeyParams = useMemo(() => {
    if (!currentQuestionId) return null
    return { attemptId, questionId: currentQuestionId }
  }, [attemptId, currentQuestionId])

  useEffect(() => {
    if (!storageKeyParams) {
      setAnnotations({})
      return
    }
    setAnnotations(readQuestionAnnotations(storageKeyParams))
  }, [storageKeyParams])

  const persistAnnotations = useCallback(
    (next: QuestionAnnotations) => {
      setAnnotations(next)
      if (storageKeyParams) {
        writeQuestionAnnotations(storageKeyParams, next)
      }
    },
    [storageKeyParams],
  )

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges()
  }, [])

  const getSelectionWithinAnnotatable = useCallback(() => {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed)
      return null
    const range = selection.getRangeAt(0)

    const common = range.commonAncestorContainer
    const commonEl =
      common.nodeType === Node.ELEMENT_NODE
        ? (common as Element)
        : common.parentElement
    const root = commonEl?.closest?.(
      '[data-quiz-annotatable="true"][data-quiz-annotation-block-id]',
    ) as HTMLElement | null
    if (!root) return null

    const blockId = root.dataset.quizAnnotationBlockId
    if (!blockId) return null

    const offsets = getSelectionOffsetsWithin(root)
    if (!offsets) return null

    const maxLength = root.textContent?.length ?? 0
    return { blockId, ...offsets, maxLength }
  }, [])

  const annotateSelection = useCallback(
    (params: { kind: AnnotationKind; mode: "add" | "remove" }) => {
      if (!storageKeyParams) return
      const info = getSelectionWithinAnnotatable()
      if (!info) return false

      const next =
        params.mode === "add"
          ? addAnnotationToBlock({
              existing: annotationsRef.current,
              blockId: info.blockId,
              kind: params.kind,
              start: info.start,
              end: info.end,
              maxLength: info.maxLength,
            })
          : removeAnnotationFromBlock({
              existing: annotationsRef.current,
              blockId: info.blockId,
              kind: params.kind,
              start: info.start,
              end: info.end,
              maxLength: info.maxLength,
            })

      persistAnnotations(next)
      clearSelection()
      return true
    },
    [
      clearSelection,
      getSelectionWithinAnnotatable,
      persistAnnotations,
      storageKeyParams,
    ],
  )

  const clearAllAnnotations = useCallback(() => {
    if (!storageKeyParams) return
    persistAnnotations(clearAllQuestionAnnotations())
    clearSelection()
  }, [clearSelection, persistAnnotations, storageKeyParams])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const isTypingTarget =
        target?.isContentEditable ||
        tag === "input" ||
        tag === "textarea" ||
        tag === "select"
      if (isTypingTarget) return

      if (event.key === "Enter") {
        event.preventDefault()
        if (showConfirmSubmitRef.current) {
          if (submitCountdownRef.current > 0) return
          handleSubmitRef.current()
          return
        }
        if (showUnansweredConfirmRef.current) {
          setShowUnansweredConfirm(false)
          if (isLastQuestionRef.current) {
            openSubmitConfirmationRef.current()
          } else {
            handleNextRef.current()
          }
          return
        }
        if (hasSelectedAnswerRef.current) {
          if (isLastQuestionRef.current) {
            openSubmitConfirmationRef.current()
          } else {
            handleNextRef.current()
          }
        } else {
          setShowUnansweredConfirm(true)
        }
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key === "Backspace") {
        event.preventDefault()
        clearAllAnnotations()
        return
      }

      if (
        event.key === "Backspace" &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey &&
        !showConfirmSubmitRef.current &&
        !showUnansweredConfirmRef.current &&
        canGoBackRef.current
      ) {
        event.preventDefault()
        handlePreviousRef.current()
        return
      }

      if (event.key === "Escape") {
        clearSelection()
        return
      }

      // Number (1-4) or letter (a-d) shortcuts to select an option.
      // Only fires when no text is selected (text selection is reserved for
      // annotation shortcuts h/x) and no modifier keys are held.
      if (
        !showConfirmSubmitRef.current &&
        !showUnansweredConfirmRef.current &&
        !isSavingAnswerRef.current &&
        !event.altKey &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey
      ) {
        const sel = window.getSelection()
        if (!sel || sel.isCollapsed) {
          const optionShortcuts: Record<string, number> = {
            "1": 0,
            "2": 1,
            "3": 2,
            "4": 3,
            "5": 4,
            a: 0,
            b: 1,
            c: 2,
            d: 3,
            e: 4,
          }
          const idx = optionShortcuts[event.key.toLowerCase()]
          if (idx !== undefined && idx < currentOptionsCountRef.current) {
            handleAnswerSelectRef.current(idx)
            event.preventDefault()
            return
          }
        }
      }

      const key = event.key.toLowerCase()
      if (key === "h" && !event.altKey && !event.metaKey && !event.ctrlKey) {
        const applied = annotateSelection({
          kind: "highlight",
          mode: event.shiftKey ? "remove" : "add",
        })
        if (applied) {
          event.preventDefault()
        }
        return
      }

      if (key === "x" && !event.altKey && !event.metaKey && !event.ctrlKey) {
        const applied = annotateSelection({
          kind: "strike",
          mode: event.shiftKey ? "remove" : "add",
        })
        if (applied) {
          event.preventDefault()
        }
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [annotateSelection, clearAllAnnotations, clearSelection])

  // Loading state
  if (isLoading) {
    return (
      <Container centerContent py={20}>
        <Spinner size="xl" />
        <Text mt={4}>Loading quiz...</Text>
      </Container>
    )
  }

  // Error state
  if (error) {
    return (
      <Container centerContent py={20}>
        <Alert.Root status="error">
          <Alert.Title>Error loading quiz</Alert.Title>
          <Alert.Description>
            {error.message || "An error occurred while loading the quiz"}
          </Alert.Description>
        </Alert.Root>
      </Container>
    )
  }

  if (!currentQuestion) {
    return (
      <Container centerContent py={20}>
        <AppEmptyState
          title={emptyStateCopy.quizzes.noData.title}
          description={emptyStateCopy.quizzes.noData.description}
        />
      </Container>
    )
  }

  // Results page
  if (isSubmitted && results) {
    // If results are scheduled and not yet available
    if (!results.can_review && results.show_result_mode === "scheduled") {
      return (
        <Container maxW="4xl" py={10}>
          <VStack gap={6} align="stretch">
            <Heading size="2xl" textAlign="center">
              Quiz Submitted Successfully!
            </Heading>

            <Card.Root>
              <Card.Body>
                <VStack gap={4} align="stretch">
                  <Text fontSize="lg" textAlign="center" color="gray.600">
                    Thank you for completing the quiz. Your answers have been
                    submitted.
                  </Text>

                  <Alert.Root status="info" mt={4}>
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Results Scheduled</Alert.Title>
                      <Alert.Description>
                        Your results, score, and detailed review will be
                        available on{" "}
                        <strong>
                          {results.result_date
                            ? new Date(results.result_date).toLocaleString()
                            : "the scheduled date"}
                        </strong>
                        . You will be able to review your answers and see the
                        correct answers at that time.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                </VStack>
              </Card.Body>
            </Card.Root>
          </VStack>
        </Container>
      )
    }

    // Show scores for immediate results
    return (
      <Container maxW="4xl" py={10}>
        <VStack gap={6} align="stretch">
          <Heading size="2xl" textAlign="center">
            Quiz Complete!
          </Heading>

          <Card.Root>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="lg" fontWeight="medium">
                    Your Score:
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {results.score} / {results.total_questions}
                  </Text>
                </HStack>

                {results.percentile !== null &&
                  results.percentile !== undefined && (
                    <HStack justify="space-between">
                      <Text fontSize="lg" fontWeight="medium">
                        Percentile:
                      </Text>
                      <Text fontSize="2xl" fontWeight="bold">
                        {results.percentile.toFixed(1)}%
                      </Text>
                    </HStack>
                  )}

                <Text color="gray.600" mt={4}>
                  You answered {results.score} out of {results.total_questions}{" "}
                  questions correctly.
                </Text>

                {results.percentile !== null &&
                results.percentile !== undefined ? (
                  <Alert.Root status="info" mt={4}>
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>About percentile ranks</Alert.Title>
                      <Alert.Description>
                        Your percentile rank may change as more students
                        complete this quiz. Percentiles are recalculated
                        automatically when new submissions come in.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : (
                  <Alert.Root status="info" mt={4}>
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Percentile not yet available</Alert.Title>
                      <Alert.Description>
                        Percentile rankings will be available once at least 100
                        students have completed this quiz.
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                )}

                {results.can_review && (
                  <Text color="blue.600" mt={2} fontWeight="medium">
                    Redirecting to review page...
                  </Text>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    )
  }

  // Quiz taking page
  return (
    <DrawerRoot
      placement="start"
      open={navigatorOpen}
      onOpenChange={(e) => setNavigatorOpen(e.open)}
    >
      <Container maxW={{ base: "4xl", lg: "7xl" }} py={10}>
        {/* Keep the navigator trigger accessible while scrolling on smaller screens. */}
        <Box
          ref={navigatorTriggerRef}
          position="sticky"
          top="0"
          zIndex={10}
          display={{ base: "flex", lg: "none" }}
          w="fit-content"
          ml="auto"
          justifyContent="flex-end"
          pointerEvents="none"
          bg="bg"
          borderRadius="md"
          p={1}
          shadow="sm"
        >
          <DrawerTrigger asChild>
            {compactNavigatorTrigger ? (
              <IconButton
                aria-label="Questions"
                size="xs"
                variant="outline"
                pointerEvents="auto"
              >
                <Icon as={FiList} />
              </IconButton>
            ) : (
              <Button size="sm" variant="outline" pointerEvents="auto">
                Questions
              </Button>
            )}
          </DrawerTrigger>
        </Box>
        <VStack gap={6} align="stretch">
          {/* Header */}
          <Box>
            <HStack justify="space-between">
              <Text color="gray.600">
                Question {quizState.currentQuestionIndex + 1} of{" "}
                {quiz.questions.length}
              </Text>
              <HStack
                gap={3}
                align="center"
                color={
                  quizState.timeRemaining !== null &&
                  quizState.timeRemaining < 60
                    ? "red.500"
                    : "gray.600"
                }
                fontWeight="medium"
              >
                <Text>
                  <small>
                    Answered {answeredCount} of{" "}
                    {answeredCount + unansweredCount}
                  </small>
                </Text>
                {quizState.timeRemaining !== null && (
                  <Text>Time: {formatTime(quizState.timeRemaining)}</Text>
                )}
              </HStack>
            </HStack>
            {enableStudentTimer && (
              <StudentTimerControl
                attemptId={attemptId}
                questionId={currentQuestion.id}
                questionStartedAt={quizState.startTime}
                instructorTimeLimited={quizState.timeRemaining !== null}
                isSubmitted={isSubmitted}
                isSavingAnswer={isSavingAnswer}
              />
            )}
          </Box>

          <HStack align="start" gap={6}>
            {/* Desktop left navigator (lg+) */}
            <Box
              display={{ base: "none", lg: "block" }}
              flex="0 0 280px"
              maxW="280px"
            >
              <QuestionNavigator quiz={quiz} maxHeightPx={questionCardHeight} />
            </Box>

            {/* Question + choices */}
            <Box ref={questionCardRef} flex="1" minW={0}>
              <Card.Root>
                <Card.Body>
                  <VStack gap={6} align="stretch">
                    {/* Question Text */}
                    <Box>
                      <HStack justify="space-between" align="start" mb={2}>
                        <Box flex="1" minW={0}>
                          {currentQuestion.section?.name && (
                            <Badge colorPalette="purple" display="inline-flex">
                              {currentQuestion.section.name}
                            </Badge>
                          )}
                        </Box>
                        <DialogRoot lazyMount>
                          <DialogTrigger asChild>
                            <Button
                              aria-label="Keyboard shortcuts"
                              colorPalette="teal"
                              data-compact-trigger="true"
                              fontSize="xs"
                              fontWeight="semibold"
                              h="auto"
                              minH="unset"
                              px={2}
                              py={1}
                              size="xs"
                              variant="outline"
                            >
                              <FiHelpCircle />
                              Shortcuts
                            </Button>
                          </DialogTrigger>
                          <DialogContent maxW="lg">
                            <DialogHeader>
                              <DialogTitle>Keyboard shortcuts</DialogTitle>
                            </DialogHeader>
                            <DialogCloseTrigger />
                            <DialogBody>
                              <VStack gap={3} align="stretch">
                                <Text color="gray.700">
                                  <strong>1–4</strong> or <strong>A–D</strong> —
                                  select an answer option
                                </Text>
                                <Text color="gray.700">
                                  <strong>Enter</strong> — next question
                                  &nbsp;·&nbsp; <strong>Backspace</strong> —
                                  previous question
                                </Text>
                                <Text color="gray.700">
                                  Select text, then <strong>H</strong> /{" "}
                                  <strong>X</strong> — highlight / strike.{" "}
                                  <strong>Shift+H</strong> /{" "}
                                  <strong>Shift+X</strong> to remove,{" "}
                                  <strong>Cmd/Ctrl+Backspace</strong> to clear
                                  all marks.
                                </Text>
                              </VStack>
                            </DialogBody>
                          </DialogContent>
                        </DialogRoot>
                      </HStack>

                      <AnnotatableContainer
                        blockId="stem"
                        ranges={annotations.stem ?? []}
                        contentKey={currentQuestion.id}
                      >
                        <HtmlContent
                          fontSize="lg"
                          fontWeight="medium"
                          mb={4}
                          content={currentQuestion.question_text}
                        />
                      </AnnotatableContainer>
                    </Box>

                    {/* Answer Options */}
                    <RadioGroup
                      key={currentQuestion.id}
                      disabled={isSavingAnswer}
                      value={
                        selectedAnswerIndex !== null
                          ? selectedAnswerIndex.toString()
                          : ""
                      }
                      onValueChange={(value) => {
                        const rawValue = value.value
                        if (typeof rawValue === "string") {
                          handleAnswerSelect(Number.parseInt(rawValue, 10))
                        }
                      }}
                    >
                      <VStack gap={3} align="stretch">
                        {currentQuestion.options.map((option, index) => {
                          const isSelected = selectedAnswerIndex === index
                          const isRecorded =
                            quizState.answers[currentQuestion.id] === index
                          const isActive =
                            isSelected ||
                            (selectedAnswerIndex === null && isRecorded)
                          const borderColor = isActive
                            ? selectedBorderColor
                            : defaultBorderColor
                          const bgColor = isActive ? selectedBg : "transparent"

                          return (
                            <Box
                              key={index}
                              p={4}
                              border="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              cursor="pointer"
                              bg={bgColor}
                              color={optionTextColor}
                              _hover={{
                                borderColor: hoverBorderColor,
                                bg: isActive ? selectedBg : hoverBg,
                              }}
                              onClick={() => {
                                if (isSavingAnswer) return
                                const selection = window.getSelection()
                                if (selection && !selection.isCollapsed) return
                                handleAnswerSelect(index)
                              }}
                            >
                              <Radio value={index.toString()}>
                                <HStack gap={2} align="flex-start">
                                  <Text
                                    as="span"
                                    fontWeight="semibold"
                                    flexShrink={0}
                                  >
                                    {String.fromCharCode(65 + index)}.
                                  </Text>
                                  <AnnotatableContainer
                                    as="span"
                                    blockId={`option:${index}`}
                                    ranges={
                                      annotations[`option:${index}`] ?? []
                                    }
                                    contentKey={currentQuestion.id}
                                  >
                                    {stripOptionPrefix(option)}
                                  </AnnotatableContainer>
                                </HStack>
                              </Radio>
                            </Box>
                          )
                        })}
                      </VStack>
                    </RadioGroup>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Box>
          </HStack>

          {/* Navigation */}
          {answerSaveError && !showConfirmSubmit && (
            <Alert.Root status="error" mb={3}>
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>Answer not saved</Alert.Title>
                <Alert.Description>{answerSaveError}</Alert.Description>
              </Alert.Content>
            </Alert.Root>
          )}
          <HStack justify="space-between">
            <Button
              onClick={handlePrevious}
              disabled={
                isSavingAnswer ||
                quizState.currentQuestionIndex === 0 ||
                (showRationaleMode === "after_each" &&
                  Object.keys(quizState.answers).length > 0)
              }
              variant="outline"
            >
              Previous
            </Button>

            <HStack gap={2}>
              {quizState.currentQuestionIndex < quiz.questions.length - 1 ? (
                <Button
                  onClick={handleNext}
                  colorPalette="blue"
                  disabled={shouldDisableNextButton || isSavingAnswer}
                  loading={isSavingAnswer}
                >
                  Next
                </Button>
              ) : (
                <Button onClick={openSubmitConfirmation} colorPalette="green">
                  Submit Quiz
                </Button>
              )}
            </HStack>
          </HStack>

          {/* Submit Confirmation Dialog */}
          {showConfirmSubmit && (
            <Box
              role="dialog"
              aria-label="Submit Quiz?"
              position="fixed"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={1000}
            >
              <Card.Root maxW="md">
                <Card.Header>
                  <Heading size="lg">Submit Quiz?</Heading>
                </Card.Header>
                <Card.Body>
                  <Text>
                    Are you sure you want to submit your quiz? You won't be able
                    to change your answers after submitting.
                  </Text>
                  <Text mt={2} color="gray.600">
                    Answered: {Object.keys(quizState.answers).length} /{" "}
                    {totalQuestions}
                  </Text>
                  {unansweredQuestionCount > 0 && (
                    <Alert.Root status="warning" mt={3}>
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>
                          {unansweredQuestionCount} unanswered question
                          {unansweredQuestionCount === 1 ? "" : "s"}
                        </Alert.Title>
                        <Alert.Description>
                          These will be marked incorrect. You can go back and
                          answer them before submitting.
                          {submitCountdown > 0 && (
                            <> You can submit in {submitCountdown} seconds.</>
                          )}
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                  {answerSaveError && (
                    <Alert.Root status="error" mt={3}>
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Title>Answer not saved</Alert.Title>
                        <Alert.Description>{answerSaveError}</Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  )}
                </Card.Body>
                <Card.Footer>
                  <HStack justify="flex-end" w="full">
                    <Button
                      variant="outline"
                      onClick={() => setShowConfirmSubmit(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorPalette="green"
                      onClick={handleSubmit}
                      disabled={
                        submitCountdown > 0 || isSubmitting || isSavingAnswer
                      }
                      loading={isSubmitting || isSavingAnswer}
                    >
                      Submit
                    </Button>
                  </HStack>
                </Card.Footer>
              </Card.Root>
            </Box>
          )}
          {/* Unanswered Question Confirmation Dialog */}
          {showUnansweredConfirm && (
            <Box
              position="fixed"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={1000}
            >
              <Card.Root maxW="md">
                <Card.Header>
                  <Heading size="lg">Question Unanswered</Heading>
                </Card.Header>
                <Card.Body>
                  <Text>
                    You haven't selected an answer for this question. Move on
                    anyway?
                  </Text>
                </Card.Body>
                <Card.Footer>
                  <HStack justify="flex-end" w="full">
                    <Button
                      variant="outline"
                      onClick={() => setShowUnansweredConfirm(false)}
                    >
                      Stay
                    </Button>
                    <Button
                      colorPalette="blue"
                      onClick={() => {
                        setShowUnansweredConfirm(false)
                        if (isLastQuestionRef.current) {
                          openSubmitConfirmation()
                        } else {
                          handleNextRef.current()
                        }
                      }}
                    >
                      Continue
                    </Button>
                  </HStack>
                </Card.Footer>
              </Card.Root>
            </Box>
          )}
        </VStack>
      </Container>

      {/* Mobile drawer navigator (md and down) */}
      <DrawerBackdrop display={{ base: "block", lg: "none" }} />
      <DrawerContent
        maxW="320px"
        display={{ base: "flex", lg: "none" }}
        h="100dvh"
        maxH="100dvh"
        flexDir="column"
        overflow="hidden"
      >
        <DrawerCloseTrigger />
        <DrawerBody flex="1" minH="0" overflowY="auto" pt={12}>
          <QuestionNavigator
            quiz={quiz}
            onNavigate={() => setNavigatorOpen(false)}
          />
        </DrawerBody>
      </DrawerContent>
    </DrawerRoot>
  )
}

export function QuizInterface(props: QuizInterfaceProps) {
  const {
    quiz,
    attemptId,
    timeLimit,
    initialAnswers,
    initialTimeRemaining,
    initialQuestionIndex,
    onAnswerSelect,
    onSubmit,
    isSubmitted,
    onQuestionChange,
    showRationaleMode,
    onNavigateToReview,
    ...contentProps
  } = props

  return (
    <QuizInterfaceProvider
      quiz={quiz}
      attemptId={attemptId}
      timeLimit={timeLimit}
      initialAnswers={initialAnswers}
      initialTimeRemaining={initialTimeRemaining}
      initialQuestionIndex={initialQuestionIndex}
      onAnswerSelect={onAnswerSelect}
      onSubmit={onSubmit}
      isSubmitted={isSubmitted}
      isSubmitting={contentProps.isSubmitting}
      onQuestionChange={onQuestionChange}
      showRationaleMode={showRationaleMode}
      onNavigateToReview={onNavigateToReview}
    >
      <QuizInterfaceContent
        {...contentProps}
        isSubmitted={isSubmitted}
        showRationaleMode={showRationaleMode}
        quiz={quiz}
        attemptId={attemptId}
      />
    </QuizInterfaceProvider>
  )
}
